import { useState, useEffect, useCallback } from 'react';
import {
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  KeyRound,
  FlaskConical,
  Radio,
  Send,
} from 'lucide-react';
import { api } from '../services/api';
import {
  PlatformSettings,
  PlatformSettingsUpdate,
  SettingField,
  SettingSecretValue,
} from '../types';
import { Card } from './Card';
import { Button } from './Button';
import { Badge } from './Badge';
import { Input } from './Input';
import { Select } from './Select';
import { Alert } from './Alert';
import { Loader } from './Loader';

/** Scopes a field can be edited under. Unscoped fields have a single value. */
type Scope = 'global' | 'sandbox' | 'live';

function isSecretValue(v: string | SettingSecretValue | undefined): v is SettingSecretValue {
  return typeof v === 'object' && v !== null && 'is_set' in v;
}

interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  help?: string;
  disabled?: boolean;
}

function Toggle({ checked, onChange, label, help, disabled }: ToggleProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        style={{
          flexShrink: 0,
          width: 40,
          height: 22,
          marginTop: 2,
          borderRadius: 'var(--radius-full)',
          border: '1px solid var(--border-default)',
          background: checked ? 'var(--accent)' : 'var(--bg-inset)',
          position: 'relative',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          transition: 'background var(--duration-fast) var(--ease-standard)',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 20 : 2,
            width: 16,
            height: 16,
            borderRadius: 'var(--radius-full)',
            background: checked ? '#fff' : 'var(--text-tertiary)',
            transition: 'left var(--duration-fast) var(--ease-standard)',
          }}
        />
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)', fontWeight: 'var(--fw-medium)' }}>
          {label}
        </span>
        {help && (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
            {help}
          </span>
        )}
      </div>
    </div>
  );
}

interface ModeSwitchProps {
  /** The mode that actually runs — the stored value, or the pending edit. */
  current: 'sandbox' | 'live';
  onChange: (next: 'sandbox' | 'live') => void;
  disabled?: boolean;
}

function ModeSwitch({ current, onChange, disabled }: ModeSwitchProps) {
  const opts = [
    { key: 'sandbox' as const, label: 'Sandbox', icon: <FlaskConical size={12} /> },
    { key: 'live' as const, label: 'Live', icon: <Radio size={12} /> },
  ];
  return (
    <div
      style={{
        display: 'inline-flex',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      {opts.map((o) => {
        const active = current === o.key;
        const isLive = o.key === 'live';
        return (
          <button
            key={o.key}
            type="button"
            disabled={disabled}
            onClick={() => onChange(o.key)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              fontSize: 'var(--text-xs)',
              fontFamily: 'var(--font-body)',
              fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
              background: active
                ? isLive
                  ? 'var(--danger-tint)'
                  : 'var(--accent-soft)'
                : 'transparent',
              color: active
                ? isLive
                  ? 'var(--danger)'
                  : 'var(--accent)'
                : 'var(--text-secondary)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              border: 'none',
            }}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function ParametersPanel() {
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [emailTest, setEmailTest] = useState<{ ok: boolean; text: string } | null>(null);
  const [testing, setTesting] = useState(false);

  // Pending edits, kept separate from `settings` so unchanged fields are never
  // submitted — which is what stops a masked secret placeholder being written
  // back over a real credential.
  const [edits, setEdits] = useState<Record<Scope, Record<string, string>>>({
    global: {},
    sandbox: {},
    live: {},
  });

  // Which credential set the scoped fields are being edited under. Independent
  // of the active mode, so live keys can be entered while still on sandbox.
  const [editScope, setEditScope] = useState<'sandbox' | 'live'>('sandbox');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.admin.getSettings();
      setSettings(data);
      setEditScope(data.active_scope);
      setEdits({ global: {}, sandbox: {}, live: {} });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load platform parameters');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty =
    Object.keys(edits.global).length > 0 ||
    Object.keys(edits.sandbox).length > 0 ||
    Object.keys(edits.live).length > 0;

  const setEdit = (scope: Scope, key: string, value: string) => {
    setEdits((prev) => ({ ...prev, [scope]: { ...prev[scope], [key]: value } }));
    setNotice(null);
  };

  const scopeOf = (field: SettingField): Scope => (field.scoped ? editScope : 'global');

  /** Current value for a field: a pending edit if present, else the stored one. */
  const valueOf = (field: SettingField): string => {
    const scope = scopeOf(field);
    const pending = edits[scope][field.key];
    if (pending !== undefined) return pending;
    const stored = field.values[scope];
    return isSecretValue(stored) ? '' : (stored ?? '');
  };

  const secretState = (field: SettingField): SettingSecretValue => {
    const stored = field.values[scopeOf(field)];
    return isSecretValue(stored) ? stored : { is_set: false, hint: '' };
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload: PlatformSettingsUpdate = {};
      if (Object.keys(edits.global).length) payload.values = edits.global;
      if (Object.keys(edits.sandbox).length) payload.sandbox = edits.sandbox;
      if (Object.keys(edits.live).length) payload.live = edits.live;

      const updated = await api.admin.updateSettings(payload);
      setSettings(updated);
      setEdits({ global: {}, sandbox: {}, live: {} });
      setNotice('Parameters saved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save parameters');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setTesting(true);
      setEmailTest(null);
      const res = await api.admin.sendTestEmail();
      setEmailTest({
        ok: res.ok,
        text: res.ok ? `Test email sent to ${res.sent_to}. Check that inbox.` : res.detail,
      });
    } catch (err) {
      setEmailTest({ ok: false, text: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  const renderField = (field: SettingField) => {
    const scope = scopeOf(field);

    if (field.kind === 'boolean') {
      return (
        <div key={field.key} style={{ gridColumn: '1 / -1' }}>
          <Toggle
            label={field.label}
            help={field.help}
            disabled={saving}
            checked={valueOf(field) === 'true'}
            onChange={(next) => setEdit(scope, field.key, next ? 'true' : 'false')}
          />
        </div>
      );
    }

    if (field.kind === 'select') {
      return (
        <Select
          key={field.key}
          label={field.label}
          hint={field.help}
          disabled={saving}
          value={valueOf(field)}
          options={field.options.map((o) => ({ value: o.value, label: o.label }))}
          onChange={(e) => setEdit(scope, field.key, e.target.value)}
          style={{ gridColumn: field.help ? '1 / -1' : undefined }}
        />
      );
    }

    if (field.kind === 'secret') {
      const stored = secretState(field);
      return (
        <div key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Input
            label={field.label}
            type="password"
            mono
            autoComplete="new-password"
            disabled={saving}
            value={valueOf(field)}
            placeholder={stored.is_set ? `Stored — ${stored.hint}` : 'Not set'}
            hint={field.help}
            onChange={(e) => setEdit(scope, field.key, e.target.value)}
          />
          {stored.is_set && (
            <span
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-tertiary)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <CheckCircle2 size={12} color="var(--success)" />
              A credential is stored. Leave blank to keep it.
            </span>
          )}
        </div>
      );
    }

    return (
      <Input
        key={field.key}
        label={field.required ? `${field.label} *` : field.label}
        disabled={saving}
        value={valueOf(field)}
        placeholder={field.placeholder}
        hint={field.help}
        onChange={(e) => setEdit(scope, field.key, e.target.value)}
        style={field.help ? { gridColumn: '1 / -1' } : undefined}
      />
    );
  };

  if (loading && !settings) {
    return <Loader size={36} label="Loading platform parameters…" />;
  }

  if (!settings) {
    return (
      <Alert tone="danger" title="Parameters unavailable" icon={<AlertCircle size={18} />}>
        {error || 'Could not load platform parameters.'}
      </Alert>
    );
  }

  const modeField = settings.groups
    .flatMap((g) => g.fields)
    .find((f) => f.prominent);
  // The pending mode edit wins over the stored mode, so the switch and the badge
  // both preview what is about to be saved.
  const modePending = modeField ? edits.global[modeField.key] : undefined;
  const modeCurrent: 'sandbox' | 'live' =
    modePending === 'live' ? 'live' : modePending === 'sandbox' ? 'sandbox' : settings.active_scope;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      {error && (
        <Alert tone="danger" title="Could not save" icon={<AlertCircle size={18} />}>
          {error}
        </Alert>
      )}
      {notice && !error && (
        <Alert tone="success" title="Done" icon={<CheckCircle2 size={18} />}>
          {notice}
        </Alert>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            Electronic invoicing is currently running against
          </span>
          <Badge tone={modeCurrent === 'live' ? 'danger' : 'neutral'} dot>
            {modeCurrent === 'live' ? 'LIVE' : 'SANDBOX'}
          </Badge>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
            Active environment
          </span>
          {modeField && (
            <ModeSwitch
              current={modeCurrent}
              disabled={saving}
              onChange={(next) => setEdit('global', modeField.key, next)}
            />
          )}
          <Button
            variant="secondary"
            size="sm"
            icon={<RefreshCw size={14} />}
            onClick={load}
            disabled={saving}
          >
            Reload
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<Save size={14} />}
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      {settings.groups.map((group) => {
        const hasScoped = group.fields.some((f) => f.scoped);

        return (
          <Card key={group.id}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12,
                marginBottom: 'var(--space-4)',
              }}
            >
              <h3
                style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--fw-semibold)',
                  color: 'var(--text-primary)',
                }}
              >
                {group.label}
              </h3>

              {hasScoped && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {(() => {
                    const modeKey = group.fields.find((f) => f.scoped)?.mode_key;
                    const active = modeKey ? settings.active_scopes?.[modeKey] : undefined;
                    return active ? (
                      <Badge tone={active === 'live' ? 'danger' : 'neutral'} dot>
                        {active === 'live' ? 'LIVE' : 'SANDBOX'}
                      </Badge>
                    ) : null;
                  })()}
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                    Editing credentials for
                  </span>
                  <div
                    style={{
                      display: 'inline-flex',
                      border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius-md)',
                      overflow: 'hidden',
                    }}
                  >
                    {(['sandbox', 'live'] as const).map((scope) => {
                      const active = editScope === scope;
                      return (
                        <button
                          key={scope}
                          type="button"
                          onClick={() => setEditScope(scope)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 12px',
                            fontSize: 'var(--text-xs)',
                            fontFamily: 'var(--font-body)',
                            fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
                            background: active ? 'var(--accent-soft)' : 'transparent',
                            color: active ? 'var(--accent)' : 'var(--text-secondary)',
                            cursor: 'pointer',
                            border: 'none',
                          }}
                        >
                          {scope === 'sandbox' ? <FlaskConical size={12} /> : <Radio size={12} />}
                          {scope === 'sandbox' ? 'Sandbox' : 'Live'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {hasScoped && editScope === 'live' && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <Alert tone="warning" title="Live credentials" icon={<KeyRound size={16} />}>
                  {group.id === 'stripe'
                    ? 'These keys take real payments from your customers. '
                    : 'These keys transmit real invoices to the tax authority and to your customers. '}
                  Sandbox and live values are stored separately, so saving here does not
                  affect sandbox.
                </Alert>
              </div>
            )}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: 'var(--space-4)',
                alignItems: 'start',
              }}
            >
              {group.fields.filter((field) => !field.prominent).map(renderField)}
            </div>

            {group.id === 'email' && (
              <div
                style={{
                  marginTop: 'var(--space-4)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-3)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Send size={14} />}
                    onClick={handleTestEmail}
                    disabled={testing || saving || dirty}
                  >
                    {testing ? 'Sending…' : 'Send test email'}
                  </Button>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                    {dirty
                      ? 'Save your changes first: the test uses the saved settings.'
                      : 'Sends a message to your own address using the saved settings.'}
                  </span>
                </div>
                {emailTest && (
                  <Alert
                    tone={emailTest.ok ? 'success' : 'danger'}
                    icon={emailTest.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                  >
                    {emailTest.text}
                  </Alert>
                )}
              </div>
            )}
          </Card>
        );
      })}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 10,
          paddingBottom: 'var(--space-4)',
        }}
      >
        <Button
          variant="primary"
          icon={<Save size={16} />}
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </div>
  );
}
